import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useSEO } from "../hooks/useSEO";

const HERO =
  "https://images.unsplash.com/photo-1609081144289-eacc3108cd03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxtb25vY2hyb21lJTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3ODQyOTg4NTh8MA&ixlib=rb-4.1.0&q=85";
const CAT1 = "https://images.pexels.com/photos/8251251/pexels-photo-8251251.jpeg";
const CAT2 =
  "https://images.unsplash.com/photo-1724582586495-d050726cf354?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxtb25vY2hyb21lJTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3ODQyOTg4NTh8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useSEO({
    title: "Nesta | Affordable, High Quality Furniture for Modern Living",
    description:
      "Shop affordable, high quality modern furniture at Nesta — minimalist sofas, chairs, beds, dining and outdoor pieces. Beautiful design, honest prices, free UK delivery.",
  });

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 grid md:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-sm uppercase tracking-luxe text-[#999] mb-6">Modern Living · Est. Nesta</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight mb-8">
            Furniture that <br /> quiets the room.
          </h1>
          <p className="text-[#666] text-lg max-w-md leading-relaxed mb-10">
            A curated collection of warm, minimalist pieces — sourced from makers who care, delivered to
            your door.
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/shop"
              data-testid="hero-shop-btn"
              className="group inline-flex items-center gap-3 bg-[#1a1a1a] text-white px-8 py-4 text-sm uppercase tracking-luxe hover:bg-[#333] transition-colors"
            >
              Shop Collection
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          className="aspect-[4/5] overflow-hidden bg-[#f5f5f0]"
        >
          <img src={HERO} alt="Nesta living room" className="w-full h-full object-cover" />
        </motion.div>
      </section>

      {/* Marquee ribbon */}
      <section className="bg-[#f5f5f0] grain relative py-6 overflow-hidden">
        <div className="marquee-track font-serif text-2xl md:text-3xl text-[#1a1a1a]/80">
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i} className="flex">
              {["Sustainable Materials", "Minimalist Design", "Modern Living"].map(
                (t) => (
                  <span key={t} className="mx-8 flex items-center gap-8">
                    {t} <span className="text-[#999]">—</span>
                  </span>
                )
              )}
            </span>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="text-sm uppercase tracking-luxe text-[#999] mb-3">Selected Works</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Featured Pieces</h2>
          </div>
          <Link to="/shop" className="hidden md:flex items-center gap-2 text-sm uppercase tracking-luxe hover:opacity-50">
            View all <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* Bento categories */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24 grid md:grid-cols-2 gap-6">
        <Link to="/shop" className="relative overflow-hidden group aspect-[3/2] bg-[#f5f5f0]">
          <img src={CAT1} alt="Living Room" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-black/10 flex items-end p-8">
            <div className="text-white">
              <div className="text-xs uppercase tracking-luxe mb-2">Collection</div>
              <div className="font-serif text-3xl">The Japandi Room</div>
            </div>
          </div>
        </Link>
        <Link to="/shop" className="relative overflow-hidden group aspect-[3/2] bg-[#f5f5f0]">
          <img src={CAT2} alt="Monochrome" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-black/10 flex items-end p-8">
            <div className="text-white">
              <div className="text-xs uppercase tracking-luxe mb-2">Collection</div>
              <div className="font-serif text-3xl">Monochrome Sets</div>
            </div>
          </div>
        </Link>
      </section>
    </div>
  );
}
